import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

/**
 * Drop-in replacement for ng2-currency-mask with natural left-to-right typing.
 * Same selector `[currencyMask]` and `[options]` / `[max]` / `[min]` inputs.
 */
@Directive({
    selector: '[currencyMask]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NaturalCurrencyMaskDirective),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NaturalCurrencyMaskDirective),
            multi: true,
        },
    ],
    standalone: false
})
export class NaturalCurrencyMaskDirective
  implements ControlValueAccessor, Validator, OnInit
{
  @Input() options: any = {};
  @Input() max: number = 99999999.99;
  @Input() min: number = -99999999.99;

  private el: HTMLInputElement;
  private isFocused = false;
  private precision = 2;
  private decimal = '.';
  private thousands = ',';
  private prefix = '';
  private suffix = '';
  private allowNegative = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef) {
    this.el = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.applyOptions();
  }

  /** Merge options input into local config. */
  private applyOptions(): void {
    if (!this.options) return;
    if (this.options.precision != null) {
      this.precision =
        typeof this.options.precision === 'string'
          ? parseInt(this.options.precision, 10)
          : this.options.precision;
    }
    if (this.options.decimal != null) this.decimal = this.options.decimal;
    if (this.options.thousands != null) this.thousands = this.options.thousands;
    if (this.options.prefix != null) this.prefix = this.options.prefix;
    if (this.options.suffix != null) this.suffix = this.options.suffix;
    if (this.options.allowNegative != null)
      this.allowNegative = this.options.allowNegative;
    if (this.options.max != null) this.max = this.options.max;
    if (this.options.min != null) this.min = this.options.min;
    if (this.options.align) {
      this.el.style.textAlign = this.options.align;
    }
  }

  // ── ControlValueAccessor ──

  writeValue(value: any): void {
    // While focused, the input already shows the correct value from onInput.
    // Skip reformatting to prevent "5" → "5.00" mid-typing.
    if (this.isFocused) return;

    if (value == null || value === '') {
      this.el.value = '';
      return;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) {
      this.el.value = '';
      return;
    }
    this.el.value = this.formatFull(num);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.disabled = isDisabled;
  }

  // ── Validator ──

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    if (val == null || val === '') return null;
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return null;
    if (num > this.max) return { max: true };
    if (num < this.min) return { min: true };
    return null;
  }

  // ── Host listeners ──

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const raw = this.el.value;
    const cursorPos = this.el.selectionStart || 0;

    // Count commas before cursor before formatting
    const commasBefore = (raw.substring(0, cursorPos).match(/,/g) || []).length;

    // Strip everything except digits, decimal, and leading minus
    let cleaned = this.stripToNumber(raw);

    // Enforce single decimal point and limit decimal digits
    const parts = cleaned.split(this.decimal);
    let integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1] : null;

    if (decimalPart != null) {
      decimalPart = decimalPart.substring(0, this.precision);
    }

    // Add thousands separators to integer part
    const isNeg = integerPart.startsWith('-');
    let digits = isNeg ? integerPart.substring(1) : integerPart;
    // Remove leading zeros (but keep at least one digit)
    digits = digits.replace(/^0+(?=\d)/, '');
    const formattedInt = this.addThousandsSeparators(digits);
    const signedInt = isNeg ? '-' + formattedInt : formattedInt;

    let formatted =
      decimalPart != null
        ? signedInt + this.decimal + decimalPart
        : signedInt;

    this.el.value = formatted;

    // Count commas after formatting and adjust cursor
    const commasAfter = (formatted.substring(0, cursorPos + (this.countCommasInFormatted(formatted, cursorPos, commasBefore))).match(/,/g) || []).length;
    const commaOffset = commasAfter - commasBefore;
    const newPos = Math.max(0, cursorPos + commaOffset);
    this.el.setSelectionRange(newPos, newPos);

    // Parse and emit numeric value
    const numericStr = formatted.replace(/,/g, '');
    const num = numericStr === '' || numericStr === '-' ? null : parseFloat(numericStr);
    this.onChange(num);
  }

  @HostListener('blur')
  onBlur(): void {
    this.isFocused = false;
    this.onTouched();
    const raw = this.el.value;
    if (raw === '' || raw == null) {
      this.onChange(null);
      return;
    }

    const cleaned = raw.replace(/,/g, '');
    let num = parseFloat(cleaned);
    if (isNaN(num)) {
      this.el.value = '';
      this.onChange(null);
      return;
    }

    // Clamp to [min, max]
    if (num > this.max) num = this.max;
    if (num < this.min) num = this.min;

    this.el.value = this.formatFull(num);
    this.onChange(num);
  }

  @HostListener('focus')
  onFocus(): void {
    this.isFocused = true;
    // Select all if value is 0 for easy overwrite
    const raw = this.el.value.replace(/,/g, '');
    const num = parseFloat(raw);
    if (num === 0) {
      setTimeout(() => this.el.select());
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text/plain') || '';
    // Clean pasted text: strip $, commas, non-numeric (except . and -)
    const cleaned = this.stripToNumber(pasted.replace(/[$,]/g, ''));

    if (cleaned === '' || cleaned === '-') {
      this.el.value = '';
      this.onChange(null);
      return;
    }

    const num = parseFloat(cleaned);
    if (isNaN(num)) return;

    // Clamp
    const clamped = Math.min(this.max, Math.max(this.min, num));
    this.el.value = this.formatFull(clamped);
    this.onChange(clamped);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Allow: navigation, delete, backspace, tab, escape, enter, home, end, arrows
    const allowed = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    ];
    if (allowed.includes(event.key)) return;

    // Allow Ctrl/Cmd + A, C, V, X, Z
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    // Allow minus if negative is allowed and cursor is at start
    if (event.key === '-' && this.allowNegative) {
      const pos = this.el.selectionStart || 0;
      if (pos === 0 && !this.el.value.includes('-')) return;
      event.preventDefault();
      return;
    }

    // Allow decimal point (once)
    if (event.key === this.decimal) {
      if (this.el.value.includes(this.decimal)) {
        event.preventDefault();
      }
      return;
    }

    // Allow digits
    if (event.key >= '0' && event.key <= '9') return;

    // Block everything else
    event.preventDefault();
  }

  // ── Formatting helpers ──

  /** Format a number with fixed decimals and thousands separators. */
  private formatFull(num: number): string {
    const fixed = num.toFixed(this.precision);
    const parts = fixed.split('.');
    const intFormatted = this.addThousandsSeparators(
      parts[0].replace('-', '')
    );
    const sign = num < 0 ? '-' : '';
    return parts.length > 1
      ? sign + this.prefix + intFormatted + this.decimal + parts[1] + this.suffix
      : sign + this.prefix + intFormatted + this.suffix;
  }

  /** Add thousands separators to an integer string. */
  private addThousandsSeparators(intStr: string): string {
    if (!this.thousands) return intStr;
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousands);
  }

  /** Strip input to only digits, one decimal, and optional leading minus. */
  private stripToNumber(value: string): string {
    let result = '';
    let hasDecimal = false;
    let hasSign = false;

    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '-' && !hasSign && i === 0 && this.allowNegative) {
        result += ch;
        hasSign = true;
      } else if (ch === this.decimal && !hasDecimal) {
        result += ch;
        hasDecimal = true;
      } else if (ch >= '0' && ch <= '9') {
        result += ch;
      }
    }
    return result;
  }

  /** Estimate cursor offset from comma changes. */
  private countCommasInFormatted(
    formatted: string,
    originalPos: number,
    commasBefore: number
  ): number {
    // Simple approach: count commas up to a position near originalPos
    let commaCount = 0;
    const searchEnd = Math.min(formatted.length, originalPos + 2);
    for (let i = 0; i < searchEnd; i++) {
      if (formatted[i] === this.thousands) commaCount++;
    }
    return commaCount - commasBefore;
  }
}
